import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output, SimpleChanges } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ThemePalette } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'eq-button-file-input',
  templateUrl: './button-file-input.component.html',
  styleUrl: './button-file-input.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    HttpClientModule,
    MatIconModule
  ],
})
export class ButtonFileInputComponent {
  @Output() filesUploaded: EventEmitter<{ key: string, location: string, url: string, fileName: string }[]> = new EventEmitter<{ key: string, location: string, url: string, fileName: string }[]>();
  @Output() fileBeingUploaded: EventEmitter<{ fileName: string }> = new EventEmitter<{ fileName: string }>();
  /** Emitted when the upload request fails so the parent can clear loading placeholders. */
  @Output() fileUploadFailed: EventEmitter<{ fileName: string }> = new EventEmitter<{ fileName: string }>();
  /** Emitted when the entire batch (all selected files) has finished uploading or failed. */
  @Output() uploadBatchComplete: EventEmitter<void> = new EventEmitter<void>();

  @Input() disabled: boolean = false;
  @Input() multiple: boolean = false;
  @Input() accept: string = "*/*";
  @Input() supportedFormats: string = "JPEG, PNG, PDF";
  @Input() header: string = "";
  @Input() submitButtonText: string = "UPLOAD & REPLACE";
  @Input() path: string = "/";
  @Input() isUploadHeaderRequired: boolean = true;
  @Input() classNames: any;
  @Input() type: string = "";
  @Input() isResetRequiredAfterUpload: boolean = false;

  @Input() confirmationRequired: boolean = false;
  @Output() confirmationEvent = new EventEmitter<any>();
  @Input() uploadConfirmation: boolean = false;
  
  files!: File | File[];
  uploadedFileCount: number = 0;
  uploadedFiles: { key: string, location: string, url: string, fileName: string }[] = [];
  bufferValue: number = 100;
  isFileUploadingStart: boolean = false;

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment: any
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['uploadConfirmation']?.currentValue) {
      if(this.uploadConfirmation) {
        this.upload();
      }
    }
  }

  filesChanged(event: any) {
    const fileList = event.target.files as FileList;
    if (!fileList?.length) return;
    this.files = this.multiple && fileList.length > 1 ? Array.from(fileList) : fileList[0];
    if (this.confirmationRequired) {
      this.confirmationEvent.emit(this.files);
    }
  }

  upload() {
    const rawFiles = Array.isArray(this.files) ? this.files : [this.files];
    const files = rawFiles.filter((f): f is File => f != null && f instanceof File);
    if (!files.length) {
      this.uploadConfirmation = false;
      this.uploadBatchComplete.emit();
      return;
    }

    files.forEach((file) => this.fileBeingUploaded.emit({ fileName: file.name }));

    const uploads$ = files.map((file) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('path', this.path);
      return this.http.post(`${this.environment.apiUrl}/file/upload`, formData).pipe(
        map((event: any) => ({
          key: event?.key,
          location: event?.location,
          url: event?.url,
          fileName: file.name
        })),
        catchError((err: HttpErrorResponse) => {
          console.log(err);
          this.fileUploadFailed.emit({ fileName: file.name });
          return of(null);
        })
      );
    });

    forkJoin(uploads$).subscribe((results) => {
      const successful = results.filter((r): r is NonNullable<typeof r> => r !== null);
      if (successful.length) {
        this.uploadedFiles.push(...successful);
        this.filesUploaded.emit([...this.uploadedFiles]);
      }
      this.uploadConfirmation = false;
      if (this.isResetRequiredAfterUpload) {
        this.uploadedFiles = [];
      }
      this.uploadBatchComplete.emit();
    });
  }
}
